import { storeToRefs } from 'pinia';
import { computed, defineComponent, nextTick, onMounted, ref, watch, } from 'vue';
import { usePayoutAccountsStore } from '@/store/modules/payout-accounts';
import { usePaymentStore } from '@/store/modules/payment';
import useTransactionsStore from '@/store/modules/transactions';
import { useAuthStore } from '@/store/modules/auth';
import { Button, WidgetDropDownSingleValue, WidgetInput, } from '@yellowcard/components-library-vue3';
import LoaderComponent from '@/components/general/LoaderComponent.vue';
import { isObject } from '@/utils/helpers';
import { MIXPANEL_EVENTS, trackEvent } from '@/utils/mixpanel';
import router, { RoutePaths } from '@/router';
import { PayoutAccountsTypes } from '@/types/payout-accounts';
import { PaymentTypes } from '@/types/payment';
import { WidgetApiServiceTypes } from '@/types/widgetApiService';
var PayoutComponents = PayoutAccountsTypes.PayoutComponents;
var PayoutFlow = PayoutAccountsTypes.PayoutFlow;
export default defineComponent({
    name: 'BankForm',
    components: {
        LoaderComponent,
        Button,
        WidgetDropDownSingleValue,
        WidgetInput,
    },
    setup() {
        const payoutAccountsStore = usePayoutAccountsStore();
        const { activePayoutAccount, currentStep, error, fromTransaction, loading, isNG, isUpdateFlow, manualBankName, networks, paymentNetworks, payoutAccounts, payoutFlow, rules, selectedPayoutMethod, selectedPaymentNetwork, } = storeToRefs(payoutAccountsStore);
        const paymentStore = usePaymentStore();
        const { currentStep: paymentCurrentStep, sellPaymentAccountName, sellPaymentAccountNumber, sellPaymentBranchCode, } = storeToRefs(paymentStore);
        const transactionsStore = useTransactionsStore();
        const { loadingMessage, channel } = storeToRefs(transactionsStore);
        const authStore = useAuthStore();
        const { partnerId } = storeToRefs(authStore);
        const isButtonDisabled = ref(true);
        const accountNameKey = ref(0);
        const bankRule = (value) => manualBankName.value
            ? rules.value.accountName(value)
            : rules.value.selectedBankName(value);
        const validateInputs = (newInput) => !(bankRule(newInput.accountBankName) &&
            rules.value.accountName(newInput.accountName) === true &&
            rules.value.bankAccountNumber(newInput.accountNumber) === true);
        onMounted(async () => {
            loadingMessage.value = '';
            isButtonDisabled.value = validateInputs({
                accountBankName: manualBankName.value
                    ? activePayoutAccount.value.accountBankName
                    : activePayoutAccount.value.networkName,
                accountNumber: activePayoutAccount.value.accountNumber,
                accountName: activePayoutAccount.value.accountName,
            });
            if (!fromTransaction.value) {
                const channels = await transactionsStore.getWithdrawChannelsWithoutFees(WidgetApiServiceTypes.ChannelTypes.BANK);
                if (channels.length) {
                    channel.value = { id: channels[0].id };
                }
            }
            await payoutAccountsStore.getNetworks();
            if (manualBankName.value) {
                payoutAccountsStore.setActivePayoutAccount({
                    networkId: paymentNetworks.value[0].id,
                    networkName: paymentNetworks.value[0].name,
                });
            }
            if (isUpdateFlow.value) {
                // set selected network value so the branch code dropdown can be populated
                if (paymentNetworks.value.length &&
                    activePayoutAccount.value.networkName) {
                    selectedPaymentNetwork.value =
                        paymentNetworks.value.find(network => network.name === activePayoutAccount.value.networkName) ?? null;
                }
            }
        });
        const branchCodes = computed(() => {
            if (!selectedPaymentNetwork.value)
                return [];
            if (isObject(selectedPaymentNetwork.value.code)) {
                return Object.keys(selectedPaymentNetwork.value.code).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
            }
            return [];
        });
        const loadingSelectOptions = computed(() => {
            if (paymentNetworks.value?.length)
                return '';
            return loading.value;
        });
        const bankInputProps = computed(() => sellPaymentAccountName.value || activePayoutAccount.value.accountName);
        const loadingAccountName = computed(() => {
            if (manualBankName.value)
                return undefined;
            const { accountNumber, networkName } = activePayoutAccount.value;
            if (accountNumber && networkName && !bankInputProps.value && isNG.value) {
                return 'Loading account name...';
            }
            return undefined;
        });
        const savePayoutAccount = async () => {
            trackEvent(MIXPANEL_EVENTS.SELL.PAYOUT_DETAILS, {
                partner: partnerId.value,
                payment_method: selectedPayoutMethod.value,
            });
            if (!fromTransaction.value)
                return;
            // if coming from transaction flow
            const accounts = payoutAccounts.value.filter(account => account.accountType === selectedPayoutMethod.value);
            if (payoutFlow.value === PayoutFlow.Create && accounts.length) {
                // when "use new payout account" option is clicked
                await payoutAccountsStore.addPayoutAccount();
                selectedPaymentNetwork.value = {
                    id: activePayoutAccount.value.networkId,
                };
                await transactionsStore.getWalletDetails();
                return;
            }
            // if no saved payout account exists for bank
            paymentCurrentStep.value =
                PaymentTypes.SellPaymentSteps.SAVE_PAYOUT_ACCOUNT;
            await router.push(RoutePaths.Payment);
        };
        const saveButtonsCTA = computed(() => [
            {
                disabled: isButtonDisabled.value || !!loading.value,
                text: loading.value || loadingAccountName.value || 'Save',
                onClick: async (event) => {
                    event.preventDefault();
                    await payoutAccountsStore.addPayoutAccount();
                    currentStep.value = PayoutComponents.ManagePayouts;
                    return payoutAccountsStore.resetActivePayoutAccount();
                },
                show: !fromTransaction.value &&
                    currentStep.value === PayoutComponents.FormSelect,
            },
            {
                disabled: isButtonDisabled.value || !!loading.value,
                text: loading.value || loadingAccountName.value || 'Continue',
                onClick: savePayoutAccount,
                show: fromTransaction.value &&
                    currentStep.value === PayoutComponents.FormSelect,
            },
        ]);
        const updateButtonsCTA = computed(() => [
            {
                disabled: isButtonDisabled.value || !!loading.value,
                text: loading.value || 'Update',
                onClick: async (event) => {
                    event.preventDefault();
                    await payoutAccountsStore.updatePayoutAccount();
                    currentStep.value = PayoutComponents.ManagePayouts;
                    payoutAccountsStore.resetActivePayoutAccount();
                },
                show: true,
            },
            {
                disabled: !!loading.value,
                color: 'danger',
                text: 'Delete',
                variant: 'outline',
                onClick: () => {
                    currentStep.value = PayoutComponents.DeleteConfirmation;
                },
                show: true,
            },
        ]);
        const buttonsCTA = computed(() => isUpdateFlow.value ? updateButtonsCTA.value : saveButtonsCTA.value);
        const inputProps = computed(() => isNG.value
            ? { readonly: true, key: accountNameKey.value }
            : { readonly: false });
        const onBankSelected = (event) => {
            const selectedNetwork = paymentNetworks.value.find(network => network.name === event) ?? null;
            selectedPaymentNetwork.value = selectedNetwork;
            payoutAccountsStore.setActivePayoutAccount({
                networkId: selectedNetwork?.id,
                networkName: selectedNetwork?.name,
            });
        };
        const onBranchSelected = (event) => {
            if (!selectedPaymentNetwork.value?.code)
                return;
            const selectedCodes = selectedPaymentNetwork.value.code;
            payoutAccountsStore.setActivePayoutAccount({
                accountBranchCode: selectedCodes[event],
                accountBranchName: event,
            });
        };
        watch(activePayoutAccount.value, newInput => {
            nextTick(() => {
                isButtonDisabled.value = validateInputs({
                    accountBankName: manualBankName.value
                        ? newInput.accountBankName
                        : newInput.networkName,
                    accountNumber: newInput.accountNumber,
                    accountName: newInput.accountName,
                });
                sellPaymentAccountNumber.value = newInput.accountNumber;
                sellPaymentAccountName.value = newInput.accountName;
                sellPaymentBranchCode.value = newInput.accountBranchCode;
                accountNameKey.value += 1;
            });
        }, { deep: true });
        watch(sellPaymentAccountName, newInput => {
            activePayoutAccount.value.accountName = newInput;
        });
        return {
            activePayoutAccount,
            bankInputProps,
            bankNetworks: networks,
            branchCodes,
            buttonsCTA,
            error,
            inputProps,
            isNG,
            loadingSelectOptions,
            manualBankName,
            onBankSelected,
            onBranchSelected,
            rules,
        };
    },
});
